/**
 * useCreateInvoice — Atomic WatermelonDB writer for desktop billing.
 *
 * Subset of BigxBookApp/src/billing/useCreateInvoice.ts.
 *
 * The sale is written in three phases, because a WatermelonDB writer only
 * serializes — it does NOT roll back. `Collection.create()` commits its own
 * adapter batch as it is called, so a throw partway through leaves earlier
 * records behind:
 *
 *   Phase 1  read and validate — the store, every product's sellable stock, the
 *            customer and the promotion. Every refusal happens here, before a
 *            single row is written or an invoice number is consumed.
 *   Phase 2  prepare the invoice, its lines, its payments, the stock decrements
 *            and movements, the customer's counters and the promotion bump.
 *   Phase 3  apply all of them in ONE `db.batch()`.
 *
 * Either the whole sale lands or none of it does.
 *
 * Records are written with isSynced=false so the existing CRDT engine
 * picks them up on the next sync cycle.
 */

import { useCallback, useState } from 'react';
import { Model, Q } from '@nozbe/watermelondb';
import { getDbError, txn } from '@/db';
import { Invoice, LineItem, Payment } from '@/db/models/Billing';
import { Customer, LoyaltyTransaction } from '@/db/models/Customers';
import { GstFiling, Promotion } from '@/db/models/System';
import { Batch, Product, StockMovement } from '@/db/models/Inventory';
import { allocateBatchesFEFO, totalAllocated } from '@/inventory/batchAllocation';
import Store from '@/db/models/Store';
import { useAppStore } from '@/store/useAppStore';
import { BILLING_CREATE, BILLING_QUICK_BILL, hasPermission } from '@/rbac';
import { localHighestSequence } from './invoiceNumberRecovery';
import { getLoyaltyConfig, useCartStore } from '@/store/useCartStore';
import {
    computeLoyaltyPointsEarned,
    derivePaymentStatus,
    generateInvoiceNumber,
    toRupees,
} from './billingEngine';
import { checkBillingGate } from './useBillingSession';
import { preparePromoUsageIncrement } from './promotionService';
import { refreshOverlay } from '@/sync/liveStock';
import { scheduleSyncPush } from '@/sync/scheduleSync';
import { logActivity } from '@/utils/auditLog';
import { describeError } from '@/utils/errorMessage';
import { ensureManualItemProduct, isManualItemId } from './manualItem';
import { billPeriod, billTimestamp, filedPeriodError, toDateInput } from './billDate';

export interface CreateInvoiceResult {
    success: boolean;
    invoiceId?: string;
    invoiceNumber?: string;
    error?: string;
}

export function useCreateInvoice() {
    const [isCreating, setIsCreating] = useState(false);

    const createInvoice = useCallback(async (): Promise<CreateInvoiceResult> => {
        if (isCreating) {
            return { success: false, error: 'Invoice creation already in progress' };
        }

        // Snapshot cart synchronously before the async write
        const {
            items,
            customer,
            isInterState,
            globalDiscount,
            payments,
            notes,
            terms,
            totals,
            adjustedItems,
            paidAmountPaisa,
            appliedPromotionId,
            loyaltyPointsRedeemed,
            quickBill,
            billDate,
        } = useCartStore.getState();

        let { activeStoreId, currentUser, deviceSlot, deviceId, nextDeviceInvoiceNo } = useAppStore.getState();
        const { raiseDeviceInvoiceCounter } = useAppStore.getState();

        // ── Pre-flight ──
        if (!currentUser) return { success: false, error: 'Not authenticated. Please sign in again.' };

        // Authorisation at the WRITE boundary, not just on the route. Route
        // guards and hidden nav links are presentation; this is what a role
        // without BILLING_CREATE actually hits. It matters because the backend
        // strips tables a role may not write (SYNC_WRITE_PERMISSIONS), so an
        // unauthorised sale used to succeed locally — decrementing stock and
        // printing a receipt — and then disappear silently at the next push.
        // Failing here keeps the till honest about what it just did.
        if (!hasPermission(currentUser.role || '', BILLING_CREATE)) {
            return { success: false, error: 'Your role does not allow creating bills.' };
        }
        // A Quick Bill's hand-entered date moves a real GST sale between days
        // and return periods — checked here, at the write, like BILLING_CREATE.
        if (quickBill && !hasPermission(currentUser.role || '', BILLING_QUICK_BILL)) {
            return { success: false, error: 'Your role does not allow Quick Bills.' };
        }
        // The bill date, fixed once so the filed-period check and the stamp
        // agree. null = an ordinary sale, stamped "now" by the database.
        const billTs = quickBill ? billTimestamp(billDate) : null;
        if (!activeStoreId) return { success: false, error: 'No active store selected.' };
        if (items.length === 0) return { success: false, error: 'Cart is empty' };
        // Refuse to write into a database that cannot save. On quota exhaustion
        // WatermelonDB silently falls back to an in-memory adapter, so without
        // this check the till keeps taking sales and printing receipts while
        // nothing is persisted — every one of them lost on the next reload.
        // The same applies after another tab has overwritten this one's data.
        const dbError = getDbError();
        if (dbError) {
            return { success: false, error: `Cannot save to this device: ${dbError}` };
        }

        // ── Billing-session gate ──
        // Mirrors mobile's behaviour: if the store requires day-open and no
        // session is active, only emergency-bill roles may proceed.
        const gate = await checkBillingGate(activeStoreId, currentUser.role || '', deviceId || '');
        if (!gate.allowed) {
            return { success: false, error: gate.reason };
        }
        const billingSessionId = gate.sessionId;
        const isEmergency = gate.isEmergency;

        // #2 fix: a device MUST own a server-provisioned invoice series (deviceSlot,
        // allocated + persisted on sync) before emitting FINAL invoice numbers. The old
        // hash(deviceId) % 100 fallback was NOT unique across devices and produced
        // colliding invoice numbers.
        //
        // On what the server does with a collision: `invoices` carries
        // @@unique([storeId, invoiceNumber]), and the push pre-checks it so the
        // offending ROW is refused rather than the whole batch — the push runs in
        // one transaction, and a raw constraint violation there would abort every
        // table in it and strand this device for good. A refused invoice comes
        // back named and is renumbered locally (sync/invoiceRenumber.ts), which
        // the operator is told about because the printed copy then disagrees with
        // the books. None of that is pleasant, so: refuse to bill until activated.
        if (deviceSlot == null) {
            // Name the cause when the server gave one. A device that is online
            // and simply not registered was being told to "connect to the
            // internet once", which is advice that cannot work and sends the
            // operator looking at their wifi.
            const reason = useAppStore.getState().deviceSlotError;
            return {
                success: false,
                error: reason
                    ? `This device cannot bill yet: ${reason}`
                    : 'This device is not activated for billing yet. Connect to the internet once to activate it, then try again.',
            };
        }

        setIsCreating(true);

        // Manual (non-catalog) lines point at one hidden product row per store,
        // because line_items.product_id is a NOT NULL foreign key on the server.
        // Resolved out here: it takes its own writer, and WatermelonDB writers
        // do not nest — doing this inside would deadlock the sale.
        let manualProductId: string | null = null;
        try {
            if (items.some((it) => isManualItemId(it.product.id))) {
                manualProductId = await ensureManualItemProduct(activeStoreId!);
            }
        } catch (err) {
            setIsCreating(false);
            return {
                success: false,
                error: describeError(err, 'Could not prepare the manually added items. Please try again.'),
            };
        }

        try {
            const result = await txn(async (db) => {
                // ── Phase 1 · Read and validate. Nothing is written here. ──
                //
                // A WatermelonDB writer is a serializer, NOT a transaction:
                // `Collection.create()` commits its own adapter batch immediately
                // (it is `prepareCreate` + `batch`), and a throw afterwards
                // undoes nothing. The old order created the invoice, its lines and
                // its payments and only then checked stock, so a refused sale left
                // a CONFIRMED invoice with payments and no stock movement behind —
                // which then synced, and the retry wrote a second one.
                //
                // So: everything that can refuse the sale runs first, every write is
                // prepared, and all of them are applied in ONE db.batch().
                const store = await db.get<Store>('stores').find(activeStoreId!);

                // A Quick Bill may not land in a month whose GST return is
                // recorded as filed: it would silently change a submitted return.
                if (billTs !== null) {
                    const period = billPeriod(billTs);
                    const filings = await db.get<GstFiling>('gst_filings').query(
                        Q.where('store_id', activeStoreId!),
                        Q.where('period', period),
                        Q.where('is_deleted', false),
                    ).fetch();
                    const refusal = filedPeriodError(period, filings);
                    if (refusal) throw new Error(refusal);
                }

                // Plan each line's stock movement up front. Every throw lives here.
                const stockPlans: Array<{
                    cartItem: (typeof items)[number];
                    product: Product;
                    batchRecords: Batch[];
                    allocations: ReturnType<typeof allocateBatchesFEFO>;
                }> = [];

                for (const cartItem of items) {
                    if (cartItem.product.isService || !cartItem.product.trackInventory) continue;

                    const product = await db.get<Product>('products').find(cartItem.product.id);
                    if (product.isDeleted) {
                        console.warn(`[useCreateInvoice] Skipping stock for deleted product: ${product.id}`);
                        continue;
                    }

                    const batchRecords = await db.get<Batch>('batches').query(
                        Q.where('product_id', product.id),
                        Q.where('is_deleted', false),
                        Q.where('quantity', Q.gt(0)),
                    ).fetch();

                    // For batch-tracked products the batches are the source of truth;
                    // otherwise fall back to the product-level counter.
                    const batchTotal = batchRecords.reduce((sum, b) => sum + b.quantity, 0);
                    const effectiveAvailable = batchRecords.length > 0 ? batchTotal : product.currentStock;
                    if (effectiveAvailable < cartItem.quantity) {
                        throw new Error(
                            `Insufficient stock for "${cartItem.product.name}". ` +
                            `Available: ${effectiveAvailable}, Required: ${cartItem.quantity}`,
                        );
                    }

                    const allocations = allocateBatchesFEFO(
                        cartItem.quantity,
                        batchRecords.map((b) => ({ id: b.id, expiryDate: b.expiryDate, quantity: b.quantity })),
                    );

                    // FEFO excludes expired batches, so the total above can be satisfiable
                    // while the *sellable* total is not. Refuse rather than under-allocate.
                    if (batchRecords.length > 0 && totalAllocated(allocations) < cartItem.quantity) {
                        throw new Error(
                            `Cannot allocate ${cartItem.quantity} × "${cartItem.product.name}" — ` +
                            `only ${totalAllocated(allocations)} available in unexpired batches.`,
                        );
                    }

                    stockPlans.push({ cartItem, product, batchRecords, allocations });
                }

                // Resolve the remaining participants while we can still refuse.
                const customerRecord = customer?.id
                    ? await db.get<Customer>('customers').find(customer.id)
                    : null;
                const promotionRecord = appliedPromotionId
                    ? await db.get<Promotion>('promotions').find(appliedPromotionId)
                    : null;

                // ── Phase 2 · Prepare every write. Still nothing committed. ──
                //
                // The device sequence is consumed here rather than before validation,
                // so a refused sale no longer burns an invoice number.
                const ops: Model[] = [];

                let seqNo = nextDeviceInvoiceNo();
                let invoiceNo = generateInvoiceNumber(seqNo, store.invoicePrefix, deviceSlot);

                // Never reuse a number this device can already see.
                //
                // The counter is the fast path; this is the one that has to be
                // right. A counter living in browser storage can be cleared,
                // evicted, or simply restored behind what has already been
                // issued, and the server has no unique constraint to catch the
                // result — so a stale counter silently mints a duplicate tax
                // invoice number. That is unrecoverable once the customer has
                // the bill in hand.
                //
                // Checking costs one indexed count per sale, against a number
                // that must be unique per store for the financial year.
                const collision = await db
                    .get<Invoice>('invoices')
                    .query(Q.where('store_id', activeStoreId!), Q.where('invoice_number', invoiceNo))
                    .fetchCount();
                if (collision > 0) {
                    const highest = await localHighestSequence(activeStoreId!, deviceSlot ?? 0);
                    await raiseDeviceInvoiceCounter(highest);
                    seqNo = nextDeviceInvoiceNo();
                    invoiceNo = generateInvoiceNumber(seqNo, store.invoicePrefix, deviceSlot);
                    console.warn(
                        `[Billing] Invoice number ${generateInvoiceNumber(seqNo - 1, store.invoicePrefix, deviceSlot)} was already used on this device; sequence recovered to ${seqNo}.`,
                    );
                }
                const paymentStatus = derivePaymentStatus(totals.finalTotalPaisa, paidAmountPaisa);

                // 1. Invoice
                const invoice = db.get<Invoice>('invoices').prepareCreate((record) => {
                    record.storeId = activeStoreId!;
                    record.customerId = customer?.id ?? null;
                    record.createdById = currentUser!.id;
                    record.invoiceNumber = invoiceNo;
                    record.type = 'SALE';
                    record.referenceInvoiceId = null;
                    record.status = 'CONFIRMED';
                    record.paymentStatus = paymentStatus;
                    record.subtotal = totals.subtotal;
                    record.discountType = globalDiscount > 0 ? 'FLAT' : null;
                    record.discountValue = globalDiscount;
                    record.discountAmount = globalDiscount;
                    record.promotionId = appliedPromotionId;
                    record.taxableAmount = totals.taxableAmount;
                    record.cgstAmount = totals.cgst;
                    record.sgstAmount = totals.sgst;
                    record.igstAmount = totals.igst;
                    record.cessAmount = totals.cess;
                    record.totalTax = totals.totalTax;
                    record.roundOff = totals.roundOff;
                    record.totalAmount = totals.totalAmount;
                    record.paidAmount = toRupees(paidAmountPaisa);
                    record.dueAmount = toRupees(Math.max(0, totals.finalTotalPaisa - paidAmountPaisa));
                    record.currency = store.currency || 'INR';
                    record.notes = notes || null;
                    record.termsConditions = terms?.trim() || null;
                    record.isInterState = isInterState;
                    record.isEInvoiced = false;
                    record.irn = null;
                    record.eInvoiceData = null;
                    record.pdfUrl = null;
                    record.whatsappSent = false;
                    record.deviceId = deviceId || null;
                    record.billingSessionId = billingSessionId;
                    record.isEmergency = isEmergency;
                    record.version = 1;
                    record.isSynced = false;
                    record.isDeleted = false;
                    // Quick Bill: created_at IS the bill date (billDate.ts). The
                    // database stamps it before this builder runs, so setting it
                    // here overrides that stamp; sync keeps a device's createdAt.
                    if (billTs !== null) {
                        (record as unknown as { _setRaw(col: string, v: number): void })._setRaw('created_at', billTs);
                    }
                });
                ops.push(invoice);

                // 2. Line items
                //
                // Snapshot each product's cost as it stands right now. purchasePrice
                // is overwritten on every restock, so reading it back later would
                // rewrite this sale's margin. 0 is a legitimate cost (free sample),
                // so only a genuinely absent value becomes null.
                const costByProductId = new Map<string, number | null>(
                    items.map((it) => [
                        it.product.id,
                        typeof it.product.purchasePrice === 'number' ? it.product.purchasePrice : null,
                    ]),
                );

                // Typed names for the manual lines, keyed by their cart id.
                const nameByCartId = new Map<string, string>(
                    items
                        .filter((it) => isManualItemId(it.product.id))
                        .map((it) => [it.product.id, it.product.name]),
                );

                const lineItemCollection = db.get<LineItem>('line_items');
                for (const adjusted of adjustedItems) {
                    ops.push(lineItemCollection.prepareCreate((record: LineItem) => {
                        record.invoiceId = invoice.id;
                        // A manual line's cart id is synthetic — swap in the
                        // placeholder so the foreign key holds, and put the name
                        // the cashier typed where the bill and GSTR-1 read it.
                        const manual = isManualItemId(adjusted.productId);
                        record.productId = manual ? manualProductId! : adjusted.productId;
                        record.variantId = null;
                        record.batchId = null;
                        record.description = manual
                            ? (nameByCartId.get(adjusted.productId) ?? null)
                            : null;
                        record.hsnCode = adjusted.hsnCode;
                        record.quantity = adjusted.quantity;
                        record.unit = adjusted.unit;
                        record.unitPrice = adjusted.unitPrice;
                        const totalDiscount = toRupees(adjusted.discountAmountPaisa);
                        record.discountType = totalDiscount > 0 ? 'FLAT' : null;
                        record.discountValue = totalDiscount;
                        record.discountAmount = totalDiscount;
                        record.taxableAmount = toRupees(adjusted.taxableAmountPaisa);
                        record.gstRate = adjusted.gstRate;
                        record.cessRate = adjusted.cessRate;
                        record.cgstAmount = toRupees(adjusted.cgstPaisa);
                        record.sgstAmount = toRupees(adjusted.sgstPaisa);
                        record.igstAmount = toRupees(adjusted.igstPaisa);
                        record.cessAmount = toRupees(adjusted.cessPaisa);
                        record.totalAmount = toRupees(adjusted.totalPaisa);
                        record.costPrice = costByProductId.get(adjusted.productId) ?? null;
                        record.version = 1;
                        record.isSynced = false;
                        record.isDeleted = false;
                    }));
                }

                // 3. Payments
                // A Payment row means money received. A CREDIT tender is the opposite
                // — the customer taking the amount on their khata — and it is already
                // represented by the invoice's dueAmount and the customer's balance.
                // Writing one here double-counted it in every receipts report, and the
                // cash book (which filters nothing) reported cash nobody handed over.
                // Skipping it also makes both credit paths produce identical data:
                // an explicit CREDIT tender and a zero-tender credit sale.
                const paymentCollection = db.get<Payment>('payments');
                for (const p of payments.filter((t) => t.method !== 'CREDIT')) {
                    ops.push(paymentCollection.prepareCreate((record: Payment) => {
                        record.invoiceId = invoice.id;
                        record.method = p.method;
                        record.amount = p.amount;
                        record.transactionRef = p.reference || null;
                        record.status = 'COMPLETED';
                        // The drawer this money crossed. Day close used to infer it from
                        // the payment's invoice, which cannot see a khata payment taken
                        // today against last week's invoice.
                        record.billingSessionId = billingSessionId;
                        // Note: receivedAt is a @date field in the model; setter handled by WDB.
                        (record as unknown as { receivedAt: Date }).receivedAt = new Date();
                        record.notes = null;
                        record.version = 1;
                        record.isSynced = false;
                        record.isDeleted = false;
                    }));
                }

                // 4. Stock decrement + StockMovement (FEFO, matching mobile)
                //
                // Batch quantities are event-derived on the server from batch-tagged
                // movements. Desktop used to write `batchId = null` on every sale, so
                // `batchStockDelta` skipped it and a batch-tracked product sold here
                // never decremented its batch — anywhere. Mobile's FEFO would then
                // allocate from a batch holding no real stock: a silent oversell.
                const stockMovementCollection = db.get<StockMovement>('stock_movements');
                for (const plan of stockPlans) {
                    const { cartItem, product, batchRecords, allocations } = plan;

                    ops.push(product.prepareUpdate((record) => {
                        record.currentStock = Math.max(0, product.currentStock - cartItem.quantity);
                        record.isSynced = false;
                        record.version = product.version + 1;
                    }));

                    if (allocations.length > 0) {
                        // One batch-tagged OUT per allocation, so both the product total
                        // and the per-batch projection derive from the event log.
                        for (const allocation of allocations) {
                            const batchRecord = batchRecords.find((b) => b.id === allocation.batchId)!;
                            ops.push(batchRecord.prepareUpdate((record) => {
                                record.quantity = Math.max(0, batchRecord.quantity - allocation.allocatedQty);
                                record.isSynced = false;
                                record.version = batchRecord.version + 1;
                            }));

                            ops.push(stockMovementCollection.prepareCreate((record: StockMovement) => {
                                record.productId = cartItem.product.id;
                                record.godownId = null;
                                record.batchId = allocation.batchId;
                                record.type = 'OUT';
                                record.quantity = allocation.allocatedQty;
                                record.referenceId = invoice.id;
                                record.referenceType = 'INVOICE';
                                record.notes = `Sale / Invoice: ${invoiceNo}`;
                                record.source = 'DESKTOP';
                                record.userId = currentUser!.id;
                                record.version = 1;
                                record.isSynced = false;
                                record.isDeleted = false;
                            }));
                        }
                        continue;
                    }

                    // Non-batch product: a single product-level OUT (batch_id stays null).
                    ops.push(stockMovementCollection.prepareCreate((record: StockMovement) => {
                        record.productId = cartItem.product.id;
                        record.godownId = null;
                        record.batchId = null;
                        record.type = 'OUT';
                        record.quantity = cartItem.quantity;
                        record.referenceId = invoice.id;
                        record.referenceType = 'INVOICE';
                        record.notes = `Sale / Invoice: ${invoiceNo}`;
                        record.source = 'DESKTOP';
                        record.userId = currentUser!.id;
                        record.version = 1;
                        record.isSynced = false;
                        record.isDeleted = false;
                    }));
                }

                // Points earned on this sale, at the STORE's configured rate.
                // Desktop and web used to award Math.floor(totalAmount) — one
                // point per rupee — against a redemption fixed at one rupee per
                // point. That is 100% cashback: every sale handed back its own
                // value as store credit. The rate is the shopkeeper's decision,
                // so it now comes from settings, defaulting to 1%.
                const earnedPoints = computeLoyaltyPointsEarned(invoice.totalAmount, getLoyaltyConfig());

                // 5. Customer khata counters. Credit/partial sales increase
                // balance; all customer sales update spend/visit stats.
                if (customerRecord && customer?.id) {
                    ops.push(customerRecord.prepareUpdate((record) => {
                        record.balance = Math.max(0, customerRecord.balance + invoice.dueAmount);
                        record.totalSpend = customerRecord.totalSpend + invoice.totalAmount;
                        record.visitCount = customerRecord.visitCount + 1;
                        record.loyaltyPoints = Math.max(0, customerRecord.loyaltyPoints - loyaltyPointsRedeemed) + earnedPoints;
                        record.lastVisitAt = new Date();
                        record.version = customerRecord.version + 1;
                        record.isSynced = false;
                    }));
                    if (loyaltyPointsRedeemed > 0) {
                        ops.push(db.get<LoyaltyTransaction>('loyalty_transactions').prepareCreate((record) => {
                            record.customerId = customer.id;
                            record.type = 'REDEEM';
                            record.points = -loyaltyPointsRedeemed;
                            record.referenceId = invoice.id;
                            record.notes = `Redeemed on invoice ${invoiceNo}`;
                            record.version = 1;
                            record.isSynced = false;
                            record.isDeleted = false;
                        }));
                    }
                    if (earnedPoints > 0) {
                        ops.push(db.get<LoyaltyTransaction>('loyalty_transactions').prepareCreate((record) => {
                            record.customerId = customer.id;
                            record.type = 'EARN';
                            record.points = earnedPoints;
                            record.referenceId = invoice.id;
                            record.notes = `Earned on invoice ${invoiceNo}`;
                            record.version = 1;
                            record.isSynced = false;
                            record.isDeleted = false;
                        }));
                    }
                }

                // 6. Promotion usage — prepared, so it lands or fails with the sale.
                if (promotionRecord) {
                    ops.push(preparePromoUsageIncrement(promotionRecord));
                }

                // ── Phase 3 · One batch. Either the whole sale lands, or none of it. ──
                await db.batch(...ops);

                return { invoiceId: invoice.id, invoiceNumber: invoiceNo };
            });

            void logActivity({
                action: 'invoice_created',
                entityType: 'invoice',
                entityId: result.invoiceId,
                storeId: activeStoreId,
                userId: currentUser.id,
                changes: {
                    summary: `${billTs !== null ? `Quick Bill dated ${toDateInput(new Date(billTs))} · ` : ''}${result.invoiceNumber} · ${items.length} item${items.length === 1 ? '' : 's'} · ${payments.map((p) => p.method).join('/') || 'UNPAID'}`,
                    amount: totals.totalAmount,
                    quantity: items.reduce((sum, it) => sum + it.quantity, 0),
                    ref: result.invoiceNumber,
                    after: {
                        total: totals.totalAmount,
                        paid: toRupees(paidAmountPaisa),
                        customer: customer?.name ?? null,
                        // The log entry's own time is when the bill was really
                        // made; this is the date it carries.
                        ...(billTs !== null ? { billDate: toDateInput(new Date(billTs)) } : {}),
                    },
                },
            });

            useCartStore.getState().clearCart();

            // Phase 2: reflect this sale in the live-stock display immediately (its
            // OUT movements are now unsynced overlay until the next sync).
            void refreshOverlay();

            // Realtime: flush this sale to the server soon so peer devices learn of
            // it near-instantly. Debounced + fire-and-forget; the write is durable.
            scheduleSyncPush();

            return {
                success: true,
                invoiceId: result.invoiceId,
                invoiceNumber: result.invoiceNumber,
            };
        } catch (error: unknown) {
            console.error('[useCreateInvoice] Failed:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error creating invoice',
            };
        } finally {
            setIsCreating(false);
        }
    }, [isCreating]);

    return { createInvoice, isCreating };
}
